// TEMPORARY (COMMAND ID 030) — preview test-data reset. Remove this file and its one line in Containers.jsx to retire it.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";

const n = (v) => Number(v || 0).toLocaleString();

const Summary = ({ p }) => (
  <div className="space-y-3 text-xs text-charcoal" data-testid="test-reset-summary">
    <div>
      <p className="font-semibold">{p.containers.length} container{p.containers.length === 1 ? "" : "s"}</p>
      {p.containers.map((c) => (
        <p key={c.zc_ref || c.container_number} className="font-mono text-warmgrey" data-testid={`test-reset-container-${c.container_number}`}>{c.zc_ref} · #{c.container_number} · {c.status} · {c.lines} lines · {n(c.boxes)} boxes</p>
      ))}
    </div>
    <div className="grid grid-cols-3 gap-2" data-testid="test-reset-inventory">
      {["MOROCCO", "IN_TRANSIT", "FLORIDA"].map((loc) => (
        <div key={loc} className="rounded-md border border-border bg-background p-2">
          <p className="text-[10px] uppercase tracking-wide text-warmgrey">{loc.replace("_", " ")}</p>
          <p className="num text-base" data-testid={`test-reset-qty-${loc}`}>{n(p.inventory[loc]?.physical)} boxes</p>
          {p.inventory[loc]?.committed ? <p className="text-warmgrey">{n(p.inventory[loc].committed)} committed</p> : null}
        </div>
      ))}
    </div>
    <div>
      <p className="font-semibold">Records to clear</p>
      <p className="text-warmgrey" data-testid="test-reset-records">{Object.entries(p.records).map(([k, v]) => `${k.replace(/_/g, " ")} ${n(v)}`).join(" · ") || "none"}</p>
    </div>
    <p className="text-warmgrey" data-testid="test-reset-preserved">Kept: {p.preserved.join(", ")}.</p>
  </div>
);

export const TestResetButton = ({ onDone }) => {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [p, setP] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/test-reset/status").then((r) => setEnabled(r.data.enabled)).catch(() => {}); }, []);
  if (!enabled) return null;

  const openDialog = async () => {
    setConfirm(""); setP(null); setOpen(true);
    try { setP((await api.get("/admin/test-reset/preview")).data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/test-reset/apply", { confirm });
      const left = ["MOROCCO", "IN_TRANSIT", "FLORIDA"].map((l) => data.after.inventory[l]?.physical || 0).reduce((a, b) => a + b, 0);
      toast.success(`Test data cleared — ${data.before.containers.length} containers removed, ${left} boxes remain in inventory. Backup saved.`);
      setOpen(false); onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={openDialog} data-testid="test-reset-button" className="h-11 border-status-sold text-status-sold hover:bg-sand">
        <RotateCcw className="mr-2 h-4 w-4" /> Reset test containers
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-xl" data-testid="test-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Reset preview test data</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">Clears every test container, packing-list import, tracking event, inventory movement, order, shipment, project, proposal, sample and sourcing request in this preview. A backup is taken first.</DialogDescription>
          </DialogHeader>
          {!p ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-terracotta" /> : <Summary p={p} />}
          <div className="mt-2 flex gap-2">
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type RESET to confirm" className="h-10 bg-white" data-testid="test-reset-confirm-input" />
            <Button onClick={run} disabled={busy || !p || confirm !== "RESET"} data-testid="test-reset-confirm-button" className="h-10 bg-status-sold text-white hover:opacity-90">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset now"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
